
import React from "react";
import { format } from "date-fns";
import { pt, enUS } from "date-fns/locale";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { StudentEvent } from "../types";
import { useLanguage } from "@/components/LanguageContext";
import { useTranslations } from "@/hooks/useTranslations";
import { CalendarIcon, Clock, MapPin, Video, Globe } from "lucide-react";

interface EventDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  event: StudentEvent;
}

export const EventDetailsDialog = ({
  isOpen,
  onClose,
  event
}: EventDetailsDialogProps) => {
  const { language } = useLanguage();
  const { t } = useTranslations("schedule");
  const dateLocale = language === 'pt' ? pt : enUS;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="dropdown-opaque">
        <DialogHeader>
          <DialogTitle>{t("eventDetailsDialog.title")}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 my-4">
          <div className="bg-muted/30 p-4 rounded-lg space-y-3">
            <div className="flex items-start gap-3">
              <CalendarIcon className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium text-sm">{t("eventDetailsDialog.date")}</p>
                <p>
                  {format(new Date(event.start_time), "EEEE, dd 'de' MMMM 'de' yyyy", { locale: dateLocale })}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium text-sm">{t("eventDetailsDialog.time")}</p>
                <p>
                  {format(new Date(event.start_time), "HH:mm", { locale: dateLocale })} - 
                  {format(new Date(event.end_time), " HH:mm", { locale: dateLocale })}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              {event.is_online ? (
                <Globe className="h-5 w-5 text-blue-500 mt-0.5" />
              ) : (
                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
              )}
              <div>
                <p className="font-medium text-sm">{t("eventDetailsDialog.type")}</p>
                <p>{event.is_online ? 
                   (language === 'pt' ? 'Aula Online' : 'Online Class') : 
                   (event.title || t("eventDetailsDialog.class"))}
                </p>
              </div>
            </div>

            {event.is_online && event.meeting_url && (
              <div className="flex items-start gap-3">
                <Video className="h-5 w-5 text-blue-500 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">{language === 'pt' ? 'Link da Reunião' : 'Meeting Link'}</p>
                  <a 
                    href={event.meeting_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline"
                  >
                    {event.meeting_url}
                  </a>
                </div>
              </div>
            )}

            {event.is_video && event.video_url && (
              <div className="flex items-start gap-3">
                <Video className="h-5 w-5 text-red-500 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">{language === 'pt' ? 'Link do Vídeo' : 'Video Link'}</p>
                  <a 
                    href={event.video_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline"
                  >
                    {event.video_url}
                  </a>
                </div>
              </div>
            )}
          </div>

          {event.description && (
            <div className="mt-4">
              <h3 className="font-medium mb-2">{t("eventDetailsDialog.notes")}</h3>
              <p className="text-sm text-muted-foreground">{event.description}</p>
            </div>
          )}
        </div>
        
        <div className="flex justify-end space-x-2">
          <Button onClick={onClose}>
            {t("eventDetailsDialog.close")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
